import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ProductsContext } from '../context/products.context';
import AnnualWageWithRating from './AnnualWageWithRating';
import LinksPopover from './LinksPopover';
import DemandHoverTooltip from './DemandHoverTooltip';
import CircularRating from './CircularRating';

export const JobsTable = ({ jobs = {} }) => {
  // Destructure with default values where appropriate
  const {
    wageRating,
    growthRating,
    totalOpeningsRating,
    linkBLS,
    onetTitle = 'Unknown Title',
    onetSOC,
    avgAnnualWage,
    avgTotalOpenings,
    longtermGrowth,
    estimatedEmployment,
    avgHourlyWage,
    maxDemand
  } = jobs;

  const { userDefaults } = useContext(ProductsContext);

  // If there's no onetSOC, return nothing.
  if (!onetSOC) return null;

  // Convert longtermGrowth to a percentage if available and valid,
  // otherwise fallback to "N/A".
  const growthPercent =
    longtermGrowth != null && !isNaN(Number(longtermGrowth))
      ? `${(Number(longtermGrowth) * 100).toFixed(2)}%`
      : "";

  return (
    <>
<div className="relative border-zinc-200 border-r my-6  min-h-[40px] w-full px-2 py-2">
  
{/* Left: SOC number */}
    <span className="absolute left-2 top-1/2 -translate-y-1/2 tabular-nums text-xs ">
      {onetSOC.toString().replace(/\B(?=(\d{4})+(?!\d))/g, '-')}
    </span>
 

  {/* Right: Demand indicator */}
  <div className="absolute right-1 top-1/2 -translate-y-1/2">
  <DemandHoverTooltip onetSOC={onetSOC} onetTitle={onetTitle}> <div className='h-[60px] -mr-2 w-[40px] cursor-pointer place-content-center justify-items-center'><CircularRating demand={maxDemand} /></div></DemandHoverTooltip>
  </div> 
</div>



      {/* Cell 2: Job Title */}
      <div className="col-span-3 px-2 py-3 text-black font-sans font-medium leading-snug flex items-center">
        {onetTitle}
      </div>

      {/* Cell 3: AVG Annual Salary */}
      <div className="px-2 py-2 flex items-center ">
        <AnnualWageWithRating
          jobs={jobs}
          avgAnnualWage={avgAnnualWage}
          avgHourlyWage={avgHourlyWage}
          wageRating={wageRating}
        />
      </div>

      {/* AVG Annual Total Openings Column */}
      <div className="px-2 py-2 flex items-center">
        <div className="w-full">
          {avgTotalOpenings != null ? (
            <span className="text-xs text-zinc-700">
              {Number(avgTotalOpenings).toLocaleString()}
            </span>
          ) : (
            //For NA implementation later on
            <span className="text-xs text-zinc-500"></span>
          )}
          {totalOpeningsRating != null && (
            <div className="h-[12px] flex mt-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className={`w-full h-3 border border-white ${
                    totalOpeningsRating > i ? 'bg-sky-300' : 'bg-zinc-100'
                  } ${i === 0 ? 'rounded-l' : i === 4 ? 'rounded-r' : ''}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Growth Rate Column */}
      <div className="px-2 py-2 flex items-center">
        <div className="w-full">
          <span className="text-xs text-zinc-700">
            {growthPercent}
          </span>
          {growthRating != null && (
            <div className="h-[12px] flex mt-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className={`w-full h-3 border border-white ${
                    growthRating > i ? "bg-emerald-300" : "bg-zinc-100"
                  } ${i === 0 ? "rounded-l" : i === 4 ? "rounded-r" : ""}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Estimated Employment Column */}
      <div className="px-2 py-2 flex items-center">
        <div className="w-full">
          {estimatedEmployment != null ? (
            <span className="text-xs text-zinc-700">
              {Number(estimatedEmployment).toLocaleString()}
            </span>
          ) : (
            //For NA implementation later
            <span className="text-xs text-zinc-500"></span>
          )}
          {/* Placeholder for consistent spacing */}
          <div className="w-full h-[12px] mt-1"></div>
        </div>
      </div>

      {/* Cell 7: Links */}
      <div className="px-4 py-2 print:hidden whitespace-nowrap text-slate-300 flex items-center">
        <LinksPopover onetSOC={onetSOC} userDefaults={userDefaults} linkBLS={linkBLS} />
      </div>
    </>
  );
};

JobsTable.propTypes = {
  jobs: PropTypes.shape({
    wageRating: PropTypes.number,
    growthRating: PropTypes.number,
    totalOpeningsRating: PropTypes.number,
    linkBLS: PropTypes.string,
    onetTitle: PropTypes.string,
    onetSOC: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    avgAnnualWage: PropTypes.number,
    avgTotalOpenings: PropTypes.number,
    longtermGrowth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    estimatedEmployment: PropTypes.number,
    avgHourlyWage: PropTypes.number,
    maxDemand: PropTypes.number,
  }).isRequired,
};

export default JobsTable;